import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { from, Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';

import {
    AuthenticationRequest,
    AuthenticationResponse,
    AuthenticatorSignAssertion,
    BufferService,
    FidoService,
    MatchCriteria,
    Policy,
    Transaction
} from '@ap/fido-uaf-frontend-client';
import { ApWindowService } from '@ap/cdk';
import {
    ApAuthenticatieFidoResource,
    AuthenticatorMetadata,
    ContentTypeId,
    ExtensionId,
    PhoneNumberMetadata,
    SessionBindingInfo,
    TokenTypeId
} from '@ap/interface-apic-authenticatie-fido';
import { AuthenticatiebeheerResource, AuthenticationTask, TaskStatus } from '@ap/interface-apic-authenticatiebeheer';

import { ApIcmpErrorService } from '../icmp/icmp.service';
import { SignResponse, SignToken, SignTokenToLsmTokenMap } from '../sign.model';
import { ValidationResult, ValidationResultType } from '../validation/validation.model';
import { ApValidationResultService } from '../validation/validation.service';

@Injectable({
    providedIn: 'root'
})
export class ApTokenService extends FidoService {
    private readonly authenticatiebeheerResource = inject(AuthenticatiebeheerResource);
    private readonly authenticationResource = inject(ApAuthenticatieFidoResource);
    private readonly icmpErrorService = inject(ApIcmpErrorService);
    private readonly validationResultService = inject(ApValidationResultService);
    private readonly windowService = inject(ApWindowService);

    public static decodeEncodedText(encodedText: string): string {
        const base64: string = ApTokenService.mapBase64UrlToBase64(encodedText);
        // https://developer.mozilla.org/en-US/docs/Web/API/WindowBase64/Base64_encoding_and_decoding#Solution_2_–_escaping_the_string_before_encoding_it
        return decodeURIComponent(
            window
                .atob(base64)
                .split('')
                .map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
                .join('')
        );
    }

    private static mapBase64UrlToBase64(base64Url: string): string {
        const missingLength: number = base64Url.length % 4;
        const replaced = base64Url.replace(/_/g, '/').replace(/\+/g, '-');
        return missingLength > 0 ? replaced.concat('='.repeat(4 - missingLength)) : replaced;
    }

    public getAuthenticationStatus(actionId: string): Observable<TaskStatus> {
        return this.authenticatiebeheerResource.getAuthenticationTask(actionId).pipe(
            catchError((error: HttpErrorResponse) => this.handleError(error)),
            tap((response: AuthenticationTask) => {
                if (response === null) {
                    this.icmpErrorService.broadcast(true);
                }
            }),
            map((response: AuthenticationTask) => (response !== null ? response.status : undefined))
        );
    }

    public getAcceptedTokens(policy: Policy): SignToken[][] {
        return policy.accepted
            .map((tokens) => this.mapTokensToInternalTokens(tokens.map((token) => token.aaid[0] as TokenTypeId)))
            .reduce(
                (acc, t2) =>
                    acc.find(
                        (t1) =>
                            JSON.stringify([...t1].sort((a, b) => a.localeCompare(b))) ===
                            JSON.stringify([...t2].sort((a, b) => a.localeCompare(b)))
                    )
                        ? acc
                        : [...acc, [...t2].sort((a, b) => a.localeCompare(b))],
                [] as SignToken[][]
            );
    }

    public getAuthenticatedToken(policy: Policy): SignToken {
        for (const tokens of policy.accepted) {
            for (const token of tokens) {
                const encodedMetadata = token.exts && token.exts.find((ext) => ext.id === ExtensionId.AUTHENTICATOR_METADATA);
                if (encodedMetadata) {
                    const metadata = JSON.parse(ApTokenService.decodeEncodedText(encodedMetadata.data)) as AuthenticatorMetadata;
                    if (metadata.sessionAuthenticated) {
                        return SignTokenToLsmTokenMap.get(token.aaid[0] as TokenTypeId);
                    }
                }
            }
        }
        return null;
    }

    public isFinancialTransaction(transactions: Transaction[]): boolean {
        return (
            !!transactions &&
            !!transactions.find((transaction) => transaction.contentType === ContentTypeId.VND_DEVOLKSBANK_AUTHREQUEST_SPENDING_JSON)
        );
    }

    public getChallenge(actionId: string): Observable<SessionBindingInfo> {
        return this.authenticationResource.getOneAuthenticationTaskSessionBinding(actionId).pipe(
            catchError((error: HttpErrorResponse) => this.handleError(error)),
            tap((response) => this.icmpErrorService.broadcast(response === null))
        );
    }

    public unbindChallenge(actionId: string): Observable<SessionBindingInfo> {
        return this.authenticationResource.patchOneAuthenticationTaskSessionBinding(actionId, { is_bound: false }).pipe(
            catchError((error: HttpErrorResponse) => this.handleError(error)),
            tap((response) => this.icmpErrorService.broadcast(response === null))
        );
    }

    public getDecodedKeyIds(policy: Policy, tokenTypeId: TokenTypeId): string[] {
        return this.getKeyIds(policy, tokenTypeId).map((keyId) => ApTokenService.decodeEncodedText(keyId));
    }

    public getKeyIds(policy: Policy, tokenTypeId: TokenTypeId): string[] {
        const matchingCriteria = this.getMatchingCriteria(policy, tokenTypeId);
        return (matchingCriteria && matchingCriteria.keyIDs) || [];
    }

    public checkPolicyForMissingPhonenumber(policy: Policy): boolean {
        const phoneNumberMetadata = this.getPhoneNumberMetadata(policy);
        return phoneNumberMetadata ? this.isMissingPhoneNumberToken(phoneNumberMetadata) : false;
    }

    public isMissingPhoneNumberToken(phoneNumberMetadata: PhoneNumberMetadata) {
        return phoneNumberMetadata && phoneNumberMetadata.available === false;
    }

    public getPhoneNumberMetadata(policy: Policy): PhoneNumberMetadata {
        let matchingCriteria = this.getToken(policy, TokenTypeId.PHONE_NUMBER);
        if (matchingCriteria == null) {
            return null;
        }
        return this.getMetaPhone(matchingCriteria);
    }

    public getMetaPhone(matchingCriteria): PhoneNumberMetadata | null {
        let phoneNumberExt = matchingCriteria?.exts?.find((d) => d.id === ExtensionId.PHONE_NUMBER_METADATA);
        if (phoneNumberExt == null) {
            return null;
        }

        return JSON.parse(ApTokenService.decodeEncodedText(phoneNumberExt.data));
    }

    public async getSignature(aaid: TokenTypeId, response: string): Promise<Int8Array> {
        return [TokenTypeId.DIGICODE, TokenTypeId.PROSPECT_DIGICODE, TokenTypeId.DIGIPAS, TokenTypeId.PHONE_NUMBER].indexOf(aaid) >= 0
            ? BufferService.getBuffer(response)
            : Promise.resolve(new Int8Array(0)); // implement other signatures
    }

    public isAnonymous(policy: Policy): boolean {
        return (
            !!policy &&
            !policy.accepted.find(
                (criterias: MatchCriteria[]) => !!criterias.find((criteria: MatchCriteria) => criteria.keyIDs && criteria.keyIDs.length > 0)
            )
        );
    }

    public validate(
        authenticationRequest: AuthenticationRequest,
        salt: Int8Array,
        aaid: TokenTypeId,
        signResponse: SignResponse,
        actionId: string,
        digipasChallenge?: string
    ): Observable<ValidationResult> {
        return from(
            this.generateAuthenticationResponse(
                authenticationRequest,
                salt,
                this.getFacetId(),
                aaid,
                signResponse.keyID,
                signResponse.response
            )
        ).pipe(
            switchMap((authenticationResponse) =>
                this.validateResponse(authenticationResponse, this.isAnonymous(authenticationRequest.policy), actionId, digipasChallenge)
            )
        );
    }

    public validateResponse(
        authenticationResponse: AuthenticationResponse,
        isAnonymous: boolean,
        actionId: string,
        digipasChallenge?: string
    ): Observable<ValidationResult> {
        return of(authenticationResponse).pipe(
            // TODO: FTEAM-2074: remove map method when bug has been found by LSM
            map((response) => {
                if (digipasChallenge) {
                    response.assertions[0] = response.assertions[0] || ({} as AuthenticatorSignAssertion);
                    response.assertions[0].exts = response.assertions[0].exts || [];
                    response.assertions[0].exts.push({
                        id: 'DIGIPAS_CHALLENGE',
                        data: digipasChallenge,
                        fail_if_unknown: false
                    });
                }
                return response;
            }),
            switchMap((response: AuthenticationResponse) => this.authenticationResource.putOneAuthenticationResponse(actionId, response)),
            map(() => ({ resultType: ValidationResultType.DONE })),
            catchError((err: HttpErrorResponse) => {
                const reason = err.error && err.error.code;

                return of(this.validationResultService.getValidationResult(reason, isAnonymous));
            })
        );
    }

    public getFacetId(): string {
        const location = this.windowService.getWindow().location;
        return `${location.protocol}//${location.hostname}/`;
    }

    private handleError(error: HttpErrorResponse): Observable<any> {
        // in case an icmp error occurs (ie: no internet) we should skip the result
        return ApIcmpErrorService.isIcmpError(error) ? of(null) : throwError(() => error);
    }

    private getMatchingCriteria(policy: Policy, tokenTypeId: TokenTypeId): MatchCriteria {
        const _tokenTypeId = this.isAnonymous(policy) ? null : tokenTypeId;
        return this.getToken(policy, _tokenTypeId);
    }

    private getToken(policy: Policy, tokenTypeId: TokenTypeId): MatchCriteria {
        return policy.accepted
            .reduce((flatList, item) => [...flatList, ...item], [])
            .find((criteria) => criteria.aaid.indexOf(tokenTypeId) >= 0);
    }

    private mapTokensToInternalTokens(lsmTokens: TokenTypeId[]): SignToken[] {
        return Array.from(new Set<SignToken>(lsmTokens.map((token) => SignTokenToLsmTokenMap.get(token)).filter((token) => !!token)));
    }
}
